/**
 * Shared types for TORI Web Agent
 * 
 * Centralized type definitions used across library, web component, and dev builds.
 */

import type { AuthenticationResult } from "@azure/msal-browser";

// Re-export HostContext for convenience
export type { HostContext } from "../host-context";
export { isValidHostContext, HOST_CONTEXT_ERROR_MESSAGE } from "../host-context";

/**
 * Auth payload sent to the iframe via postMessage
 */
export interface ToriAuthPayload {
  type: "TORI_AUTH";
  accessToken: string;
  idToken: string;
  expiresOn: string | null;
  account: {
    username: string | undefined;
    homeAccountId: string | undefined;
    environment: string | undefined;
  };
  hostContext: {
    appId: string | undefined;
    platformId: string | undefined;
    portfolioId: string | undefined;
  };
    prompts?: string[];
}

export interface ToriReadyMessage {
  type: "TORI_READY";
}

export interface ToriTokenRefreshMessage {
  type: "TORI_TOKEN_REFRESH";
  reason?: string;
}

export type ToriIframeMessage = ToriReadyMessage | ToriTokenRefreshMessage;

/**
 * Props for the core ToriWidget component
 */
export interface ToriWidgetCoreProps {
  /**
   * Host context containing at least one identifier
   */
  hostContext: {
    appId?: string;
    platformId?: string;
    portfolioId?: string;
  };
  /**
   * URL of the TORI chat iframe
   */
  iframeSrc: string;
  /**
   * Title displayed in the widget header. Defaults to "TORI".
   */
  title?: string;
  /**
   * Optional custom prompt suggestions. When provided, these override the
   * default prompt topics displayed in the TORI chat interface.
   */
  prompts?: string[];
  /**
   * Optional custom trigger element. When provided, the default floating
   * action button is hidden and this element is rendered as the widget
   * open trigger instead.
   *
   * React usage:
   * ```tsx
   * <ToriWebAgent appId="my-app">
   *   <button>Ask TORI</button>
   * </ToriWebAgent>
   * ```
   */
  children?: React.ReactNode;
}

/**
 * Helper to create auth payload from MSAL result
 */
export function createAuthPayload(
  result: AuthenticationResult,
  hostContext: ToriWidgetCoreProps["hostContext"],
  prompts?: string[]
): ToriAuthPayload {
  return {
    type: "TORI_AUTH",
    accessToken: result.accessToken,
    idToken: result.idToken,
    expiresOn: result.expiresOn?.toISOString?.() ?? null,
    account: {
      username: result.account?.username,
      homeAccountId: result.account?.homeAccountId,
      environment: (result as Record<string, unknown>).environment as string | undefined,
    },
    hostContext: {
      appId: hostContext.appId,
      platformId: hostContext.platformId,
      portfolioId: hostContext.portfolioId,
    },
    prompts: prompts?.length ? prompts : undefined,
  };
}
